import { useAuth } from '@ocula/auth';
import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { OrgAuthorisedRoutes } from '@src/types';

import { createFormatter } from './createFormatter';
import { enGbFormat } from './localeDefinitions/enGb';
import { enUsFormat } from './localeDefinitions/enUs';
import { Formatter } from './types';

export interface I18nContextType {
  formatter: Formatter;
}

const fallbackTimeZone = 'Europe/London';

export const I18nContext = createContext<I18nContextType>({ formatter: createFormatter(enGbFormat, fallbackTimeZone) });

function getFormatterForOrganisationLocale(organisations?: readonly OrgAuthorisedRoutes[] | null, orgName?: string) {
  const matchingOrganisations = organisations?.filter((org) => org.url === orgName) ?? [];
  const locale = matchingOrganisations.length ? matchingOrganisations[0].locale : null;
  const defaultTimeZone = matchingOrganisations.length ? matchingOrganisations[0].defaultTimeZone : fallbackTimeZone;
  switch (locale) {
    case 'en-GB':
      return createFormatter(enGbFormat, defaultTimeZone);
    case 'en-US':
      return createFormatter(enUsFormat, defaultTimeZone);
    default:
      return createFormatter(enGbFormat, defaultTimeZone);
  }
}

/** The I18nProvider value is dependent on the orgName of the organisation being viewed. */
export function I18nProvider({ children }: { children: ReactNode }) {
  const { orgName } = useParams();
  const { organisations } = useAuth();

  const [formatter, setFormatter] = useState<Formatter>(() =>
    getFormatterForOrganisationLocale(organisations, orgName),
  );

  useEffect(() => {
    setFormatter(getFormatterForOrganisationLocale(organisations, orgName));
  }, [organisations, orgName]);

  const contextValue = useMemo(() => ({ formatter }), [formatter]);

  return <I18nContext.Provider value={contextValue}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextType {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}
